<template>
  <CustomTableCanDropDrag
    :draggable="!isChecklistStatusDone"
    :columns="columnsTableFields"
    :data="dataformat"
    :is-loading="isLoading"
    :id-item-expland="idItemExpland"
    :show-checkbox-row="isShowCheckbox"
    :reorderable="false"
    :table-type="DRAG_AND_DROP_TABLE_TYPE.SUGGESTEDACTIONS"
    :is-recommend="true"
    header-wrapper-class="!tw-bg-[#FCFCFD]"
    @update-selected-rows="updateSelectedRows($event)"
    @removed="removeData"
    @clicked-row="onClickOpenRow"
  >
    <template #cell(title)="data">
      <div class="absolute_cell tw-flex tw-justify-center tw-w-full">
        <div
          class="tw-flex tw-items-center tw-flex-1 tw-h-full tw-cursor-pointer"
          @click="(e) => onClickOpenRow(data.item, columnsTableFields[4], e)"
        >
          <div
            :class="`tw-flex-1 tw-flex tw-gap-[17px] tw-items-center ${rowClass}`"
          >
            <StatusBadge
              :value="
                data?.item?.isSuggested
                  ? statusCode.suggested
                  : statusCode.applied
              "
              class="tw-w-fit"
            />
            <div class="tw-flex">
              <div class="title">
                {{ data.value }}
                <span class="score-title"
                  >({{ data.item.checklistGroupItemCount }})</span
                >
              </div>
            </div>
            <b-button
              :disabled="isChecklistStatusDone"
              :style="
                activeId === data.item?.id && isCalling && isShowLoading
                  ? `background-color: #0b5ed7`
                  : ``
              "
              data-cy="suggested-action-table-add-button"
              variant="primary"
              :class="{
                'suggest-add-btn tw-h-[28px] tw-relative tw-flex tw-justify-center tw-items-center tw-w-fit tw-shadow-none tw-bg-[#fff] tw-text-[#387DFF] tw-px-[16px] tw-text-[11px] tw-font-medium tw-rounded-[50px]': true,
                '!tw-cursor-not-allowed !tw-bg-[#b7b7b7] !tw-border-0':
                  isChecklistStatusDone,
              }"
              @click="addAction(data.item, $event)"
            >
              <div
                v-if="activeId === data.item?.id && isCalling && isShowLoading"
                class="tw-text-[#FFF] tw-h-[28px] loading-icon"
              >
                <!-- eslint-disable vue/no-unused-refs -->
                <LottieAnimation
                  ref="anim"
                  :animation-data="ProgressAnimation"
                  :auto-play="true"
                  :loop="true"
                  :style="{ height: '28px' }"
                />
              </div>
              <span
                :style="
                  activeId === data.item?.id && isCalling && isShowLoading
                    ? `visibility: hidden`
                    : ``
                "
              >
                {{
                  getComponentUiText(
                    "app-assess-recommendations-action-table-button-title-add-1",
                  )
                }}
              </span>
            </b-button>
          </div>
        </div>
        <div class="tw-w-2 tw-h-full tw-cursor-default"></div>
      </div>
    </template>
    <template #cell(scoreIncreaseTotal)="data">
      <div class="absolute_cell tw-flex tw-justify-center tw-w-full">
        <div
          class="tw-flex tw-items-center tw-flex-1 tw-h-full tw-cursor-pointer"
          @click="(e) => onClickOpenRow(data.item, columnsTableFields[4], e)"
        >
          <ScoreIncrease
            :first-score="data.item.scoreIncreaseFirstCount"
            :total-score="data.item.scoreIncreaseTotal"
          />
        </div>
        <div class="tw-w-2 tw-h-full tw-cursor-default" />
      </div>
    </template>
    <template #cell(relativeRiskValue)="data">
      <div class="absolute_cell tw-flex tw-justify-center tw-w-full">
        <div
          class="tw-flex tw-items-center tw-justify-center tw-flex-1 tw-h-full tw-cursor-pointer"
          @click="(e) => onClickOpenRow(data.item, columnsTableFields[4], e)"
        >
          <StatusBadge
            v-if="getTagRelativeRisk(data.value, dataPercentile)"
            :value="getTagRelativeRisk(data.value, dataPercentile) ?? ''"
            class="tw-w-fit"
          />
        </div>
        <div class="tw-w-2 tw-h-full tw-cursor-default" />
        <div
          class="tw-w-[25px] tw-h-full tw-cursor-pointer"
          @click="(e) => onClickOpenRow(data.item, columnsTableFields[4], e)"
        />
      </div>
    </template>
    <template #cell(action)="data">
      <div
        class="tw-absolute tw-top-0 tw-right-0 tw-bottom-0 tw-left-0 tw-flex tw-flex-row tw-items-center"
      >
        <div class="tw-w-2 tw-h-full" />
        <div
          class="tw-relative tw-flex-1 tw-h-full tw-w-[45px] tw-flex tw-items-center"
        >
          <CellTableAction
            v-if="isShowMeatball"
            :id="
              'recommended-suggested-action-table-' +
              (data.tablePrefix || '') +
              data.item.id
            "
            class="tw-overflow-hidden tw-h-full"
            :disabled="isChecklistStatusDone"
            :value="data"
            :is-internal="false"
            :actions="tableFileActions"
            :class-button="'tw-w-[45px]'"
            @manage-user="handleActionClick"
          />
        </div>
        <div class="tw-w-2 tw-h-full" />
      </div>
    </template>

    <template #cell(expland)="data">
      <ArrowDownTableIcon
        :class="[
          data.item.id === idItemExpland ? 'show' : '',
          'tw-cursor-pointer',
        ]"
      />
    </template>

    <template #detail="data">
      <DetailRowActionTable
        :id-item-expand="idItemExpland"
        :detail-data="data"
      />
    </template>
  </CustomTableCanDropDrag>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from "vue";
import { CustomTableColumn } from "../common/customTable.vue";
import { getComponentUiText, getTagRelativeRisk } from "@/utils/utilities";
import CustomTableCanDropDrag from "@/components/common/customTableCanDropDrag.vue";
import CellTableAction from "@/components/common/setting/cellTableAction.vue";
import ScoreIncrease from "@/components/assessments/scoreIncreaseElement.vue";
import StatusBadge from "@/components/common/setting/statusBadge.vue";
import ArrowDownTableIcon from "../svgs/arrowDownTableIcon.vue";
import {
  actionClickType,
  MODAL_NAME,
  statusCode,
  DRAG_AND_DROP_TABLE_TYPE,
} from "@/utils/constants";
import DetailRowActionTable from "../recommend/detailRowActionTable.vue";
import { dataActionTableColumn } from "@/interfaces/recommendations/IActionInterfaces";
import { IModalMessage } from "@/interfaces/common/common";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { Ref } from "vue";
import { computed } from "vue";
import store from "@/store";
import ProgressAnimation from "../../assets/animation/progress_dark.json";
import { LottieAnimation } from "lottie-web-vue";

const props = defineProps({
  dataRow: {
    type: Array as () => dataActionTableColumn[],
    required: true,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    required: false,
  },
  headerClass: {
    type: String,
    required: false,
    default: "",
  },
  titleButton: {
    type: String,
    required: false,
    default: null,
  },
  rowClass: {
    type: String,
    required: false,
    default: "",
  },
  titleActionValue: {
    type: String,
    required: false,
    default: null,
  },
  isShowCheckbox: {
    type: Boolean,
    required: false,
    default: true,
  },
  isShowMeatball: {
    type: Boolean,
    required: false,
    default: true,
  },
  isChecklistStatusDone: {
    type: Boolean,
    required: false,
  },
  isCalling: {
    type: Boolean,
    required: false,
  },
  activeId: {
    type: String,
    required: false,
    default: null,
  },
  isShowLoading: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const dataformat = computed(() => {
  return dataTable.value.map((item) => {
    return {
      ...item,
      rowBgColor: item?.isSuggested ? null : "#FCFCFD",
    };
  });
});
const dataTable = ref(props.dataRow);
let currentSeletedRow: any[] = [];
const tableFileActions = [actionClickType.actionEditActionRecomendation];
const dataPercentile: Ref<any> = computed(() => {
  return store.state.Recommend.percentileChecklistGroupTemplateMetrics;
});
const emit = defineEmits(["update-selected-rows", "update-add-action"]);

const addAction = (id, event) => {
  event.stopPropagation();
  emit("update-add-action", {
    isSuggestionsAction: true,
    addedActions: [id],
  });
};

const removeData = (item) => {
  const id = item.elementId;
  if (currentSeletedRow.includes(id)) {
    dataTable.value = dataTable.value.filter(
      (item) => !currentSeletedRow.includes(item.id),
    );
  } else dataTable.value = dataTable.value.filter((item) => item.id !== id);
};

onMounted(() => {
  formatData();
});

const updateSelectedRows = (seletedRow) => {
  currentSeletedRow = seletedRow;
  emit("update-selected-rows", seletedRow);
};

const idItemExpland = ref(null);

const handleActionClick = (value: any, action: string) => {
  switch (action) {
    case actionClickType.actionEditActionRecomendation:
      onShowEditActionModal(value);
      break;
    default:
      break;
  }
};

const onShowEditActionModal = (action: any) => {
  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_RECOMMENDATION_EDIT_ACTIONS,
    properties: {
      action,
      isEditActionTable: false,
    },
  };
  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};

const columnsTableFields: CustomTableColumn[] = [
  {
    text:
      props.titleActionValue ??
      getComponentUiText("app-user-profile-form-label-title-1"),
    value: "title",
    headerClass: `tw-text-[#878787] tw-text-[10px] tw-not-italic tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase ${props.headerClass}`,
    sortable: false,
    headerWrapperId: "",
    valueClass: "!tw-relative tw-min-h-[60px] tw-cursor-pointer",
    isActiveClick: false,
  },
  {
    text: getComponentUiText(
      "app-assess-recommendations-action-table-header-score-increase-1",
    ),
    value: "scoreIncreaseTotal",
    // style center tw-w-[260px] tw-flex tw-justify-center
    headerClass:
      "tw-text-[#878787] tw-text-[10px] tw-not-italic tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase tw-pr-[40px]",
    valueClass: "!tw-w-[280px] !tw-relative tw-cursor-pointer",
    headerWrapperId: "recom-suggested-action-table-score-increase",
    sortable: true,
    sortDefault: true,
    isActiveClick: false,
  },
  {
    text: getComponentUiText(
      "app-assess-recommendations-action-table-header-relative-risk-1",
    ),
    value: "relativeRiskValue",
    headerClass:
      "tw-text-[#878787] tw-text-[10px] tw-font-semibold tw-tracking-[1px] tw-leading-[100%] tw-uppercase !tw-pr-0",
    valueClass: "!tw-w-[111px] !tw-relative",
    headerWrapperId: "recom-action-table-relative-risk",
    sortable: true,
    sortDefault: false,
  },
  {
    text: "",
    value: "action",
    headerClass: "",
    valueClass: "!tw-w-[61px] !tw-px-0 !tw-py-0 !tw-relative",
  },
  {
    text: "",
    value: "expland",
    headerClass: "",
    valueClass: "!tw-w-[12px] !tw-relative tw-cursor-pointer",
    isActiveClick: true,
  },
];

const formatData = () => {
  dataTable.value.map((obj) => ({ ...obj, isSuggestionsAction: true }));
};

watch(
  () => props.dataRow,
  () => {
    dataTable.value = props.dataRow;
    formatData();
  },
);

const onClickOpenRow = (item, col, e) => {
  if (!col.isActiveClick) return;
  if (item.id === idItemExpland.value) {
    idItemExpland.value = null;
  } else {
    idItemExpland.value = item.id;
  }
};
</script>

<style lang="scss" scoped>
.show {
  transform: rotate(180deg);
}
.title {
  color: var(--Brand-Navy, #211f58);
  font-family: Poppins;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 130%; /* 16.9px */
  margin-right: 4px;
}
.score-title {
  color: var(--Brand-Navy, #211f58);
  font-family: Poppins;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
}
.loading-icon {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}
.suggest-add-btn {
  border: 2px solid #387dff;
}
.absolute_cell {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}
</style>
